import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Role, User } from '@shared/types/User';
import { fmtDate, esc } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { AuthError } from '@infra/auth/AuthService';
import { LicenceLimitsService } from '@application/licence/LicenceLimitsService';
import { UserFormView } from '@presentation/views/forms/UserFormView';
import {
  accessibleViewLabelsForPerm,
  SHELL_PERM_LABELS,
  type ShellPerm,
} from '@application/access/ViewAccessPolicy';
import {
  listTenantUsers,
  resetTenantUserPassword,
  updateTenantUser,
} from '@application/auth/UserDirectoryService';
import { FormHelper } from '@presentation/components/FormHelper';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { openFormModal } from '@presentation/shell/openFormModal';
import { UserRoleUpdateSchema } from '@shared/validation/UserSchema';
import { onExclusive } from '@presentation/shell/viewDom';
import { mountCabinetAccessMatrix } from '@presentation/views/parametres/CabinetAccessMatrix';

const ROLE_META: Record<Role, { cls: string; label: string; perm: ShellPerm }> = {
  super_admin: { cls: 'admin', label: 'Administrateur', perm: 'admin' },
  admin: { cls: 'admin', label: 'Administrateur', perm: 'admin' },
  rh: { cls: 'rh', label: 'Gestionnaire RH', perm: 'rh' },
  comptable: { cls: 'compta', label: 'Comptable', perm: 'compta' },
  lecture: { cls: 'lecture', label: 'Lecture seule', perm: 'lecture' },
  salarie: { cls: 'salarie', label: 'Salarié', perm: 'salarie' },
};

function card(u: User, currentUserId: string): string {
  const meta = ROLE_META[u.role];
  const initials = `${(u.prenom[0] ?? '').toUpperCase()}${(u.nom[0] ?? '').toUpperCase()}`;
  return `<div class="user-card role-${meta.cls} ${u.id === currentUserId ? 'is-current' : ''}"
      data-name="${esc(`${u.prenom} ${u.nom} ${u.email}`.toLowerCase())}" data-role="${meta.cls}">
    <div class="uc-top">
      <div class="uc-avatar">${esc(initials || '?')}</div>
      <div class="uc-info">
        <div class="uc-name">${esc(u.prenom)} ${esc(u.nom)}</div>
        <div class="uc-email">${esc(u.email)}</div>
      </div>
    </div>
    <span class="uc-role-badge role-${meta.cls}">${esc(meta.label)}</span>
    <div class="uc-meta">
      <div class="uc-meta-row"><span class="uc-meta-icon">●</span> Statut : <strong>${u.actif ? 'Actif' : 'Inactif'}</strong></div>
      <div class="uc-meta-row"><span class="uc-meta-icon">●</span> Dernière connexion : <strong>${esc(fmtDate(u.derniere_connexion))}</strong></div>
    </div>
    <div class="uc-actions">
      <button type="button" class="btn btn-secondary btn-sm user-edit-role" data-user-id="${esc(u.id)}">Modifier le rôle</button>
      ${
        u.id === currentUserId
          ? ''
          : `<button type="button" class="btn ${u.actif ? 'btn-danger' : 'btn-secondary'} btn-sm user-toggle-active" data-user-id="${esc(u.id)}" data-active="${u.actif ? '1' : '0'}">${u.actif ? 'Désactiver' : 'Réactiver'}</button>`
      }
      <button type="button" class="btn btn-secondary btn-sm user-reset-password" data-user-id="${esc(u.id)}" data-user-name="${esc(`${u.prenom} ${u.nom}`)}">Réinitialiser le mot de passe</button>
    </div>
  </div>`;
}

type PasswordResetHandler = (userId: string, password: string) => Promise<void>;

export function openPasswordResetDialog(
  section: HTMLElement,
  userId: string,
  userName: string,
  resetPassword: PasswordResetHandler,
): void {
  const modal = document.createElement('nexxus-modal') as HTMLElement & {
    open: () => void;
    close: () => void;
  };
  modal.setAttribute('title', 'Réinitialiser le mot de passe');
  modal.setAttribute('size', 'sm');
  modal.innerHTML = `
    <form id="user-password-reset-form" class="modal-body" novalidate>
      <p style="margin:0 0 18px;color:var(--gris);">
        Définissez un nouveau mot de passe pour <strong>${esc(userName)}</strong>.
      </p>
      <div class="field">
        <label for="user-new-password">Nouveau mot de passe</label>
        <input id="user-new-password" type="password" minlength="12" required autocomplete="new-password" aria-describedby="user-password-requirements">
        <span class="hint" id="user-password-requirements">12 caractères minimum.</span>
      </div>
      <div class="field" style="margin-top:14px;">
        <label for="user-new-password-confirmation">Confirmer le mot de passe</label>
        <input id="user-new-password-confirmation" type="password" minlength="12" required autocomplete="new-password">
      </div>
      <p id="user-password-reset-error" role="alert" style="display:none;margin:14px 0 0;color:var(--rouge,#b42318);"></p>
      <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:22px;">
        <button type="button" class="btn btn-ghost" data-cancel>Annuler</button>
        <button type="submit" class="btn btn-primary" data-confirm>Réinitialiser</button>
      </div>
    </form>`;
  section.appendChild(modal);

  const form = modal.querySelector<HTMLFormElement>('#user-password-reset-form');
  const passwordInput = modal.querySelector<HTMLInputElement>('#user-new-password');
  const confirmationInput = modal.querySelector<HTMLInputElement>(
    '#user-new-password-confirmation',
  );
  const errorMessage = modal.querySelector<HTMLElement>('#user-password-reset-error');
  const submitButton = modal.querySelector<HTMLButtonElement>('[data-confirm]');

  const showError = (message: string): void => {
    if (!errorMessage) return;
    errorMessage.textContent = message;
    errorMessage.style.display = 'block';
  };
  const removeModal = (): void => {
    if (modal.isConnected) modal.remove();
  };

  modal.addEventListener('close', removeModal, { once: true });
  modal.querySelector('[data-cancel]')?.addEventListener('click', () => modal.close());
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const password = passwordInput?.value ?? '';
    const confirmation = confirmationInput?.value ?? '';
    if (password.length < 12) {
      showError('Le mot de passe doit contenir au moins 12 caractères.');
      passwordInput?.focus();
      return;
    }
    if (confirmation !== password) {
      showError('Les mots de passe ne correspondent pas.');
      confirmationInput?.focus();
      return;
    }
    if (!userId) {
      showError('Utilisateur introuvable. Rechargez la page puis réessayez.');
      return;
    }

    if (errorMessage) errorMessage.style.display = 'none';
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Réinitialisation…';
    }
    void resetPassword(userId, password)
      .then(() => {
        ToastService.success('Mot de passe réinitialisé');
        modal.close();
      })
      .catch((error: unknown) => {
        showError(
          error instanceof Error && error.message
            ? error.message
            : 'Réinitialisation impossible. Réessayez dans quelques instants.',
        );
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = 'Réinitialiser';
        }
      });
  });

  modal.open();
  passwordInput?.focus();
}

function renderRolesRef(section: HTMLElement, ctx: Parameters<ViewController>[1]): void {
  const grid = section.querySelector<HTMLElement>('#roles-ref-grid');
  if (!grid) return;
  const gate = ctx.getLicenceRuntime()?.featureGate;
  const profiles: ShellPerm[] = ['admin', 'rh', 'compta', 'lecture', 'salarie'];
  grid.innerHTML = profiles
    .map((perm) => {
      const screens = accessibleViewLabelsForPerm(perm, gate);
      return `<div class="roles-ref-card role-${perm}">
        <div class="roles-ref-title">${esc(SHELL_PERM_LABELS[perm])}</div>
        <div class="roles-ref-desc">${screens.length} écran(s) accessible(s) avec la licence actuelle</div>
        <ul class="roles-ref-list">${screens.map((s) => `<li>${esc(s)}</li>`).join('')}</ul>
      </div>`;
    })
    .join('');
}

export const utilisateursController: ViewController = async (section, ctx) => {
  try {
    await ctx.auth.requireRoleFresh('admin', 'super_admin');
  } catch (err) {
    if (err instanceof AuthError && err.code === 'INSUFFICIENT_ROLE') {
      ToastService.warning('Accès réservé aux administrateurs');
    }
    return;
  }

  const tenantId = ctx.tenantContext.tryGetTenant()?.id;
  let users = await listTenantUsers(ctx, tenantId);
  const currentUserId = ctx.auth.getSession()?.user_id ?? '';

  const info = section.querySelector('#users-count-info');
  if (info) info.textContent = `${users.length} compte${users.length > 1 ? 's' : ''}`;

  const kpis = section.querySelector<HTMLElement>('#users-role-kpis');
  const refreshKpis = (): void => {
    if (!kpis) return;
    const counts: Record<string, number> = {};
    for (const u of users) counts[ROLE_META[u.role].cls] = (counts[ROLE_META[u.role].cls] ?? 0) + 1;
    const order: Array<{ cls: string; label: string }> = [
      { cls: 'admin', label: 'Administrateurs' },
      { cls: 'rh', label: 'Gestionnaires RH' },
      { cls: 'compta', label: 'Comptables' },
      { cls: 'lecture', label: 'Lecture seule' },
      { cls: 'salarie', label: 'Salariés' },
    ];
    kpis.innerHTML = order
      .map(
        (o) =>
          `<div class="role-kpi-card kpi-${o.cls}"><div class="value">${counts[o.cls] ?? 0}</div><div class="label">${esc(o.label)}</div></div>`,
      )
      .join('');
  };
  refreshKpis();

  const grid = section.querySelector<HTMLElement>('#users-list');
  const renderGrid = (): void => {
    if (!grid) return;
    const q = (section.querySelector<HTMLInputElement>('#users-search')?.value ?? '')
      .toLowerCase()
      .trim();
    const roleFilter = grid.dataset.roleFilter ?? '';
    const list = users.filter((u) => {
      const meta = ROLE_META[u.role];
      if (roleFilter && meta.cls !== roleFilter) return false;
      if (q && !`${u.prenom} ${u.nom} ${u.email}`.toLowerCase().includes(q)) return false;
      return true;
    });
    grid.innerHTML =
      list.length === 0
        ? '<div style="color:var(--gris);padding:16px;">Aucun utilisateur</div>'
        : list.map((u) => card(u, currentUserId)).join('');
  };
  renderGrid();
  renderRolesRef(section, ctx);

  const matrixHost = section.querySelector<HTMLElement>('#cabinet-access-matrix');
  if (matrixHost) {
    await mountCabinetAccessMatrix(matrixHost, ctx);
  }

  const reloadUsers = async (): Promise<void> => {
    users = await listTenantUsers(ctx, tenantId);
    if (info) info.textContent = `${users.length} compte${users.length > 1 ? 's' : ''}`;
    refreshKpis();
    renderGrid();
  };

  const openRoleEditor = (user: User): void => {
    openFormModal(
      section,
      { title: `Rôle — ${user.prenom} ${user.nom}`, size: 'sm' },
      (host, close) => {
        host.innerHTML = `
        <form id="user-role-form">
          ${FormHelper.select(
            'role',
            'Profil',
            [
              { value: 'admin', label: 'Administrateur' },
              { value: 'rh', label: 'Gestionnaire RH' },
              { value: 'comptable', label: 'Comptable' },
              { value: 'lecture', label: 'Lecture seule' },
              { value: 'salarie', label: 'Salarié' },
            ],
            { required: true, value: user.role === 'super_admin' ? 'admin' : user.role },
          )}
          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
            <button type="button" class="btn btn-ghost" data-cancel>Annuler</button>
            <button type="submit" class="btn btn-primary">Enregistrer</button>
          </div>
        </form>`;
        host.querySelector('[data-cancel]')?.addEventListener('click', close);
        const form = host.querySelector<HTMLFormElement>('#user-role-form')!;
        FormHelper.bindSubmit(form, UserRoleUpdateSchema, async (data) => {
          await updateTenantUser(ctx, user.id, { role: data.role });
          ToastService.success('Rôle mis à jour');
          close();
          await reloadUsers();
        });
      },
    );
  };

  onExclusive(grid, 'click', (event) => {
    const reset = (event.target as HTMLElement).closest<HTMLButtonElement>('.user-reset-password');
    if (reset) {
      openPasswordResetDialog(
        section,
        reset.dataset.userId ?? '',
        reset.dataset.userName ?? 'cet utilisateur',
        (userId, password) => resetTenantUserPassword(ctx, userId, password),
      );
      return;
    }
    const edit = (event.target as HTMLElement).closest<HTMLButtonElement>('.user-edit-role');
    if (edit) {
      const user = users.find((u) => u.id === edit.dataset.userId);
      if (user) openRoleEditor(user);
      return;
    }
    const toggle = (event.target as HTMLElement).closest<HTMLButtonElement>('.user-toggle-active');
    if (toggle) {
      const user = users.find((u) => u.id === toggle.dataset.userId);
      if (!user) return;
      const activer = !user.actif;
      void (async () => {
        const ok = await confirmAction({
          host: section,
          title: activer ? 'Réactiver le compte' : 'Désactiver le compte',
          message: activer
            ? `${user.prenom} ${user.nom} pourra de nouveau se connecter.`
            : `${user.prenom} ${user.nom} ne pourra plus se connecter. Le compte est conservé.`,
          confirmLabel: activer ? 'Réactiver' : 'Désactiver',
          danger: !activer,
        });
        if (!ok) return;
        try {
          await updateTenantUser(ctx, user.id, { actif: activer });
          ToastService.success(activer ? 'Compte réactivé' : 'Compte désactivé');
          await reloadUsers();
        } catch (error) {
          ToastService.error(error instanceof Error ? error.message : 'Action impossible');
        }
      })();
    }
  });

  const w = window as unknown as Record<string, unknown>;
  w['filterUsers'] = () => renderGrid();
  w['setUsersRoleFilter'] = (role: string, btn: HTMLElement) => {
    if (grid) grid.dataset.roleFilter = role;
    section.querySelectorAll('.users-chip').forEach((c) => c.classList.remove('active'));
    btn.classList.add('active');
    renderGrid();
  };
  w['openUserModal'] = async () => {
    try {
      await ctx.auth.requireRoleFresh('admin', 'super_admin');
    } catch {
      ToastService.warning('Accès réservé aux administrateurs');
      return;
    }
    const check = LicenceLimitsService.canAddUser(ctx.getLicenceRuntime(), users.length);
    if (!check.allowed) {
      ToastService.warning(check.message ?? "Limite d'utilisateurs atteinte");
      return;
    }
    const modal = document.createElement('nexxus-modal') as HTMLElement & {
      open: () => void;
      close: () => void;
    };
    modal.setAttribute('title', 'Nouvel utilisateur');
    modal.setAttribute('size', 'lg');
    const host = document.createElement('div');
    modal.appendChild(host);
    section.appendChild(modal);

    const close = (): void => {
      modal.close();
      modal.remove();
    };
    UserFormView.render(
      host,
      async () => {
        users = await listTenantUsers(ctx, tenantId);
        if (info) info.textContent = `${users.length} compte${users.length > 1 ? 's' : ''}`;
        refreshKpis();
        renderGrid();
        close();
      },
      close,
    );
    modal.open();
  };
};
